import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useTheme } from '@/components/ThemeProvider';
import { useThemeSettings } from '@/hooks/useThemeSettings';
import { useLandingContent } from '@/hooks/useLandingContent';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  Mountain, 
  Users, 
  Shield, 
  Compass,
  Sun,
  Moon,
  Tent,
  Map,
  Heart,
  Star,
  Award,
  Sparkles,
  Route,
  MapPin,
  Quote,
} from 'lucide-react';

// Icon registry — must match ALLOWED_FEATURE_ICONS on the backend.
const ICON_MAP = {
  Users, Shield, Compass, Mountain, Map, Tent,
  Heart, Star, Award, Sparkles, Route, MapPin,
};

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { themeSettings } = useThemeSettings();
  const { content } = useLandingContent();

  // If user is already logged in, redirect to dashboard
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const features = (content?.features?.length
    ? content.features
    : [
        { icon: 'Users', title: 'Connect with Guides', description: 'Find vetted, certified outdoor experts who match your interests and experience level.' },
        { icon: 'Shield', title: 'Trusted Community', description: 'Every guide and organization is vetted and certified by our team for your safety.' },
        { icon: 'Compass', title: 'Personalized Matches', description: 'Take our quiz and get AI-powered recommendations tailored just for you.' },
      ]
  ).map((f) => ({ ...f, IconComp: ICON_MAP[f.icon] || Compass }));

  const stats = [
    { value: '300+', label: 'Certified Partners' },
    { value: '1,200+', label: 'Adventures Hosted' },
    { value: '10K+', label: 'Happy Adventurers' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="rounded-full hidden sm:inline-flex"
              data-testid="landing-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="px-3 sm:px-4"
              data-testid="landing-login-btn"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="rounded-full px-4 sm:px-6"
              data-testid="landing-register-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={themeSettings.hero_image || "https://images.unsplash.com/photo-1758272959663-b30513083206?w=1920&q=80"}
            alt="Mountain hiking adventure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div className="animate-slide-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
              <Mountain className="h-4 w-4" />
              {content?.hero_kicker || 'Your Adventure Awaits'}
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 animate-slide-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            {content?.hero_title_line1 || 'Find Your Path'}
            <span className="block text-primary">{content?.hero_title_line2 || 'Into the Wild'}</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10 animate-slide-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            {content?.hero_subtitle || 'Connect with trusted guides, discover amazing campgrounds, and join a community that makes outdoor adventures accessible for everyone.'}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            <Button 
              size="lg" 
              className="text-xl px-10 py-8 rounded-full shadow-lg hover:shadow-xl transition-all animate-pulse-glow"
              onClick={() => navigate('/register')}
              data-testid="hero-cta-btn"
            >
              {content?.hero_cta_primary || 'Join the Adventure'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-muted-foreground/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-card border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className="text-center animate-slide-up opacity-0"
                style={{ animationDelay: `${0.1 * index}s`, animationFillMode: 'forwards' }}
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{content?.features_heading || 'How It Works'}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {content?.features_subheading || 'We make it easy to connect with the outdoor community and start your adventure with confidence.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.IconComp;
              return (
                <div
                  key={`${feature.title}-${index}`}
                  className="group relative p-8 rounded-3xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 animate-slide-up opacity-0"
                  style={{ animationDelay: `${0.1 * (index + 1)}s`, animationFillMode: 'forwards' }}
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section — optional, shown only when enabled and populated */}
      {content?.testimonials_enabled && content?.testimonials?.length > 0 && (
        <section className="py-24 px-4 md:px-8 bg-card/40" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{content.testimonials_heading}</h2>
              {content.testimonials_subheading && (
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  {content.testimonials_subheading}
                </p>
              )}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.testimonials.map((t, i) => (
                <figure
                  key={i}
                  className="p-6 rounded-2xl bg-background border border-border/50 flex flex-col"
                  data-testid={`testimonial-card-${i}`}
                >
                  <Quote className="h-6 w-6 text-primary mb-3 opacity-60" />
                  <blockquote className="text-base text-foreground/90 flex-1 mb-4">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="flex items-center gap-3">
                    {t.avatar_url ? (
                      <img
                        src={t.avatar_url}
                        alt={t.author}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold">
                        {(t.author || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-sm">{t.author}</div>
                      {t.role && <div className="text-xs text-muted-foreground">{t.role}</div>}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex justify-center gap-4 mb-8">
            <Tent className="h-12 w-12 text-primary" />
            <Map className="h-12 w-12 text-accent" />
            <Heart className="h-12 w-12 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {content?.bottom_cta_heading || 'Ready to Start Your Journey?'}
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            {content?.bottom_cta_subheading || "Join thousands of adventurers who've found their outdoor community."}
          </p>
          <Button 
            size="lg" 
            className="text-xl px-12 py-8 rounded-full shadow-lg"
            onClick={() => navigate('/register')}
            data-testid="cta-join-btn"
          >
            {content?.bottom_cta_button || 'Create Free Account'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-border/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size="small" />
          <p className="text-muted-foreground text-sm">
            © 2025 TheUnityBlaze Connection. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
